import { useCallback, useRef } from 'react';

const DEFAULT_ROOT_MARGIN = '0px';
const DEFAULT_THRESHOLD = 0;

export type UseIntersectionObserverArgs = {
	enabled?: boolean;
	onEnterIntersection?: () => void;
	onLeaveIntersection?: () => void;
	rootMargin?: string;
	threshold?: number;
};

type Node = HTMLElement | null;

export const useIntersectionObserver = (
	{ enabled = true, onEnterIntersection, onLeaveIntersection, rootMargin = DEFAULT_ROOT_MARGIN, threshold = DEFAULT_THRESHOLD }: UseIntersectionObserverArgs = {
		onEnterIntersection: () => void 0,
		onLeaveIntersection: () => void 0,
	}
) => {
	const targetRef = useRef<Node>(null);
	const rootRef = useRef<Node>(null);
	const observerRef = useRef<IntersectionObserver | null>(null);

	const observe = useCallback(() => {
		const root = rootRef.current;
		const target = targetRef.current;

		if (!target || !root) {
			return;
		}

		if (!enabled) {
			return;
		}

		const options = { root, rootMargin: rootMargin, threshold };

		const observer = new IntersectionObserver(([{ isIntersecting }]) => {
			if (isIntersecting) {
				onEnterIntersection?.();
			} else {
				onLeaveIntersection?.();
			}
		}, options);

		observer.observe(target);

		observerRef.current = observer;
	}, [enabled, onEnterIntersection, onLeaveIntersection, rootMargin, threshold]);

	const unobserve = useCallback(() => {
		observerRef.current?.disconnect();
		observerRef.current = null;
	}, []);

	const initializeObserver = useCallback(() => {
		unobserve();
		observe();
	}, [observe, unobserve]);

	const rootRefCallback = useCallback(
		(node: Node) => {
			rootRef.current = node;
			initializeObserver();
		},
		[initializeObserver]
	);

	const targetCallback = useCallback(
		(node: Node) => {
			targetRef.current = node;
			initializeObserver();
		},
		[initializeObserver]
	);

	return {
		rootRef: rootRefCallback,
		targetRef: targetCallback,
	};
};
